import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaTag } from 'react-icons/fa';

// Tag Input Component (chip input)
// `value` is a JSON string array (e.g. '["tag1","tag2"]') or ''.
const TagInputComponent = ({
  value,
  onChange,
  variant = 'compact',
  placeholder = 'Enter a tag',
  title,
  helperText = 'Press Enter to add each tag',
  showAddButton = true
}) => {
  const inputRef = useRef(null);
  const comfortable = variant === 'comfortable';
  const showHeader = !!title;
  const sizes = comfortable
    ? {
        inputPadding: '10px 12px',
        inputFontSize: 13,
        inputRadius: 8,
        chipPadding: '6px 10px',
        chipFontSize: 12,
        iconSize: 12,
        gap: 10,
        chipGap: 8
      }
    : {
        inputPadding: '4px 8px',
        inputFontSize: 11,
        inputRadius: 4,
        chipPadding: '6px 10px',
        chipFontSize: 11,
        iconSize: 8,
        gap: 6,
        chipGap: 6
      };
  const [tags, setTags] = useState(() => {
    try {
      if (value && value.trim()) {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.filter(tag => tag && typeof tag === 'string' && tag.trim()) : [];
      }
    } catch (e) {
      // Invalid JSON, treat as empty
    }
    return [];
  });
  const [inputValue, setInputValue] = useState('');

  // Sync tags when value prop changes externally
  useEffect(() => {
    try {
      if (value && value.trim()) {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          const newTags = parsed.filter(tag => tag && typeof tag === 'string' && tag.trim());
          if (JSON.stringify(newTags) !== JSON.stringify(tags)) {
            setTags(newTags);
          }
        }
      } else if (tags.length > 0) {
        setTags([]);
      }
    } catch (e) {
      // Invalid JSON, reset to empty
      if (tags.length > 0) {
        setTags([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Update parent when tags change
  useEffect(() => {
    const valueStr = tags.length > 0 ? JSON.stringify(tags) : '';
    if (valueStr !== value) {
      onChange(valueStr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags]);

  const addFromRaw = (raw) => {
    const cleaned = (raw || '').trim();
    if (!cleaned) return;
    const parts = cleaned
      .split(',')
      .map(s => s.replace(/,+$/g, '').trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    setTags(prev => {
      const next = [...prev];
      parts.forEach(p => {
        if (!next.includes(p)) next.push(p);
      });
      return next;
    });
    setInputValue('');
  };

  const handleRemoveTag = (index) => {
    setTags(prev => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFromRaw(inputValue);
    }
  };

  return (
    <div style={{ 
      width: '100%', 
      maxWidth: '100%', 
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      {showHeader && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FaTag style={{ color: '#111827' }} />
            <div style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>{title}</div>
          </div>
          {helperText && (
            <div style={{ marginTop: 8, fontSize: 14, color: '#111827', opacity: 0.9 }}>
              {helperText}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          border: 'none',
          borderRadius: 0,
          padding: 0,
          background: 'transparent',
          boxShadow: 'none'
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: sizes.chipGap, alignItems: 'center' }}>
          {tags.length === 0 && (
            <span
              style={{
                flexBasis: '100%',
                fontSize: 12,
                color: '#9ca3af',
                fontStyle: 'italic',
                marginBottom: 4
              }}
            >
              No tags found
            </span>
          )}
          {tags.map((tag, index) => {
            // Light-blue chip palette
            const bg = 'rgba(59, 130, 246, 0.14)'; // blue-500 @ ~14%
            const br = 'rgba(59, 130, 246, 0.28)'; // subtle border
            const fg = '#1d4ed8'; // blue-700
            return (
              <div
                key={index}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: sizes.chipPadding,
                  background: bg,
                  border: `1px solid ${br}`,
                  borderRadius: 999,
                  fontSize: sizes.chipFontSize,
                  color: fg,
                  fontWeight: 600
                }}
              >
                <span>{tag}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveTag(index)}
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 999,
                    border: `1px solid ${br}`,
                    background: 'rgba(59, 130, 246, 0.10)',
                    color: fg,
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    outline: 'none'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.18)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.10)';
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.25)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  title="Remove tag"
                  aria-label={`Remove tag ${tag}`}
                >
                  <FaTimes size={Math.max(10, sizes.iconSize - 2)} />
                </button>
              </div>
            );
          })}

          <div
            style={{
              flex: 1,
              minWidth: 100,
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #d1d5db',
              paddingBottom: 2,
              background: 'transparent'
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => addFromRaw(inputValue)}
              placeholder={placeholder}
              style={{
                flex: 1,
                padding: comfortable ? '6px 0' : '4px 0',
                border: 'none',
                outline: 'none',
                fontSize: sizes.inputFontSize,
                background: 'transparent',
                color: '#111827'
              }}
            />
            {/* Button removed; tags are added via Enter key only */}
          </div>
        </div>
      </div>

    </div>
  );
};

export default TagInputComponent;
